import React, {useMemo} from 'react';

import {useTranslation} from 'react-i18next';

import {ReactComponent as LeftIcon} from '../../../assets/svg/arrow-left.svg';
import {ReactComponent as RightIcon} from '../../../assets/svg/arrow-right.svg';

import {I18N_NS} from '../../../core/constants/names';
import {colors} from '../../../theme';
import {formatNumber} from '../../../utils/number';

import {Icon} from '../Icon';
import {IconButton} from '../IconButton';

import {generatePagination} from './generator';
import {Buttons, ItemButton} from './styled';

interface PaginationProps {
    currentPageIndex: number;
    lastPageIndex: number;
    onPageIndexChange: (index: number) => void;
    disabled?: boolean;
}

export const Pagination = ({
    currentPageIndex,
    lastPageIndex,
    onPageIndexChange,
    disabled = false,
}: PaginationProps) => {
    const pagination = useMemo(() => generatePagination(currentPageIndex + 1, lastPageIndex + 1), [
        currentPageIndex,
        lastPageIndex,
    ]);
    const {t} = useTranslation(I18N_NS);

    if (lastPageIndex === 0) {
        return null;
    }

    return (
        <Buttons>
            <IconButton
                label={t('components.pagination.back')}
                icon={<Icon source={LeftIcon} width={8} height={13} />}
                disabled={disabled || currentPageIndex === 0}
                color={disabled ? colors.textMuted : colors.textPrimary}
                onClick={() => {
                    onPageIndexChange(currentPageIndex - 1);
                }}
                hoverIndication
            />

            {pagination.map((value, index) => {
                const renderKey = `${value}${index}`;

                if (typeof value === 'string') {
                    return (
                        <span key={renderKey} style={{padding: '0 5px'}}>
                            ...
                        </span>
                    );
                }

                const validIndex = value - 1;
                const isActive = validIndex === currentPageIndex;

                return (
                    <ItemButton
                        key={renderKey}
                        isActive={isActive}
                        disabled={disabled || isActive}
                        onClick={() => {
                            onPageIndexChange(validIndex);
                        }}
                    >
                        {formatNumber(value)}
                    </ItemButton>
                );
            })}

            <IconButton
                label={t('components.pagination.Next')}
                icon={<Icon source={RightIcon} width={8} height={13} />}
                disabled={disabled || lastPageIndex === currentPageIndex}
                color={disabled ? colors.textMuted : colors.textPrimary}
                onClick={() => {
                    onPageIndexChange(currentPageIndex + 1);
                }}
                hoverIndication
            />
        </Buttons>
    );
};
